import Backdrop from '@mui/material/Backdrop';
import React, { FC, useState } from 'react';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwDialog from '../TwDialog/TwDialog';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import TwDropzone from '../TwDropzone/TwDropzone';
import Grid from '@mui/material/Grid';
import TwUploadDocumentsRows from './TwUploadDocumentsRows';
import DocumentUtils from '../../utils/documentUtils';
import { LoadDocumentType } from '../../redux/api/load/load.model';
import './TwUploadOrderTrip.scss';
import { OrdersTripsDocumentParams } from '../../redux/api/orders/orders.model';
import { TwSelectOption } from '../TwSelects/TwSelects';
import {
  useUploadOrderTripDocumentByTokenMutation,
  useUploadOrderTripDocumentMutation
} from '../../redux/api/orders/ordersApi';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import TwAlert from '../TwAlerts/TwAlerts';
import { useAppSelector } from '../../redux/hooks';

interface TwUploadOrderTripFilesProps {
  open: boolean;
  entity: 'order' | 'trip';
  defaultVisibility: boolean;
  entityList: TwSelectOption[];
  orderId: string;
  tripId?: string;
  handleClose: () => void;
  onAddDocumentsSuccess?: () => void;
  /**
   * This disables the upload function when clicking on Upload
   * instead it returns the files to the parent so the upload is handled
   * in the parent
   */
  disableInternalUpload: boolean;
  /**
   * 
Returns the files to the parent
   */
  handleFilesToUpload?: (files: OrdersTripsDocumentParams[]) => void;
  /**
   * hides the entity selector Trip or Order id
   */
  hideEntitySelector?: boolean;
  guestServiceToken?: string;
  entityStatus?: string;
}

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
const TwUploadOrderTripFiles: FC<TwUploadOrderTripFilesProps> = ({
  open,
  entity,
  defaultVisibility,
  entityList,
  orderId,
  tripId,
  disableInternalUpload,
  hideEntitySelector = false,
  guestServiceToken,
  handleClose,
  onAddDocumentsSuccess,
  handleFilesToUpload,
  entityStatus
}): JSX.Element => {
  const activeTrips = useAppSelector((state) => state.activeTrips);
  const { addNotification } = useNotifications();
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<OrdersTripsDocumentParams[]>([]);
  const [uploadOrderTripDocument] = useUploadOrderTripDocumentMutation();
  const [uploadOrderTripDocumentByToken] = useUploadOrderTripDocumentByTokenMutation();
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const documentTypeList = [
    { label: 'Select type', value: LoadDocumentType.empty },
    ...DocumentUtils.getDocTypeOptions()
  ];

  const handleFilesUpload = (files: File[]): void => {
    files?.map((file: File) => {
      setFiles((prev) => [
        ...prev,
        {
          document: file,
          type: LoadDocumentType.empty,
          visibility: defaultVisibility,
          entity: entity,
          orderId: orderId,
          tripId: tripId,
          name: file.name
        }
      ]);
    });
  };

  const onVisibilityChange = (name: string, visibility: boolean): void => {
    const newFiles = [...files];
    const indx = files.findIndex((file) => file.document.name === name);
    newFiles[indx].visibility = visibility;
    setFiles(newFiles);
  };

  const onTypeChange = (name: string, type: LoadDocumentType): void => {
    const newFiles = [...files];
    const indx = files.findIndex((file) => file.document.name === name);
    newFiles[indx].type = type;
    setFiles(newFiles);
  };

  const onEntityIdChange = (name: string, entityId: string): void => {
    const newFiles = [...files];
    const indx = files.findIndex((file) => file.document.name === name);

    // if uploading from order we can select trips options,
    // orderId is empty if trip id was selected from options, if no trip id was selected then we use orderId
    if (entity === 'order') {
      if (entityId === '') {
        newFiles[indx].tripId = '';
        newFiles[indx].orderId = orderId;
      } else {
        newFiles[indx].tripId = entityId;
        newFiles[indx].orderId = orderId;
      }
    }

    // uploading from a trip we can select orders options,
    // tripId is empty if order id was selected from options, if no order id was selected then we use tripId
    if (entity === 'trip') {
      if (entityId === '') {
        newFiles[indx].orderId = '';
        newFiles[indx].tripId = tripId;
      } else {
        newFiles[indx].orderId = entityId;
        newFiles[indx].tripId = tripId;
      }
    }
    setFiles(newFiles);
  };

  const close = (): void => {
    setFiles([]);
    handleClose();
  };

  const handleRemoveUploadedInMemoryFile = (item: File): void => {
    setFiles((currentFiles) => currentFiles.filter((file) => file.document.name !== item.name));
  };

  const handleFilesUploadToService = async (): Promise<void> => {
    setIsUploading(true);
    await Promise.all(
      files.map(async (d) => {
        !!guestServiceToken
          ? await uploadOrderTripDocumentByToken({
              document: d,
              token: guestServiceToken
            }).unwrap()
          : await uploadOrderTripDocument({
              document: d,
              searchTripParams: activeTrips,
              status: entityStatus
            }).unwrap();
      })
    )
      .then(() => {
        onAddDocumentsSuccess && onAddDocumentsSuccess();
      })
      .catch((error) => {
        const errorMessage =
          error.status && error.status !== 500
            ? error.data.message
            : 'Failed to upload one or many documents';
        addNotification({
          message: errorMessage,
          variant: 'error'
        });
      })
      .finally(() => {
        setIsUploading(false);
        close();
      });
  };

  const handleSubmitBtn = (): void => {
    if (disableInternalUpload) {
      handleFilesToUpload && handleFilesToUpload(files);
      setFiles([]);
    } else {
      handleFilesUploadToService();
    }
  };

  return (
    <>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 10000
        }}
        open={isUploading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <TwDialog
        open={open}
        title="Upload document"
        size="md"
        onClickClose={close}
        actions={[
          <TwButton key="1" type={TwButtonType.secondary} label="Cancel" action={close} />,
          <TwButton
            key="2"
            type={TwButtonType.primary}
            label={disableInternalUpload ? 'Add' : 'Upload'}
            disabled={isUploading}
            action={handleSubmitBtn}
          />
        ]}
      >
        <Box className="upload-document">
          {uploadErrors.length > 0 &&
            uploadErrors.map((error, i) => <TwAlert key={i} message={error} />)}
          <p className="upload-document--text">
            You can upload pdf, heic, jpeg or png under 15 mb for a single file and up to 200 Mb of
            all attachments for a load.
          </p>

          <TwDropzone
            maxSize={MAX_FILE_SIZE}
            text={'Drop file here or'}
            highlightedText={'click to upload'}
            secondaryText="Files supported: PDF, XSLS, JPG, PNG, maximum size 200 MB."
            onFileUploaded={handleFilesUpload}
            onErrors={(err: string[]): void => setUploadErrors(err)}
            acceptedFiletypes={['application/pdf', 'image/heic', 'image/jpeg', 'image/png']}
          />

          <Grid
            container
            sx={{
              backgroundColor: ' #F3F4F6',
              padding: '10px',
              '& .MuiGrid-item': {
                color: ' #9CA3AF',
                fontSize: '12px',
                fontWeight: '600'
              }
            }}
            spacing={1}
          >
            <Grid item xs={!hideEntitySelector ? 4 : 6}>
              DOCUMENT
            </Grid>
            <Grid item xs={!hideEntitySelector ? 4 : 5}>
              TYPE
            </Grid>
            {!hideEntitySelector && (
              <Grid item xs={2}>
                {entity === 'order' ? 'TRIP #' : 'ORDER #'}
              </Grid>
            )}
            <Grid item xs={!hideEntitySelector ? 2 : 1}></Grid>
          </Grid>

          {files.map((file, i) => (
            <TwUploadDocumentsRows
              key={i}
              file={file}
              entityIdsSelectList={entityList}
              documentTypeList={documentTypeList}
              entity={entity}
              handleVisibilityChange={onVisibilityChange}
              handleTypeChange={onTypeChange}
              handleIdSelector={onEntityIdChange}
              handleRemoveFile={handleRemoveUploadedInMemoryFile}
              isPublicLink={!!guestServiceToken}
              hideEntitySelector={hideEntitySelector}
            />
          ))}
        </Box>
      </TwDialog>
    </>
  );
};

export default TwUploadOrderTripFiles;
